/**
 * Reserved top-level URL slugs that can NEVER be tenant codes.
 *
 * These are paths the app owns at the root level — if a tenant were
 * allowed to use one of these as its `tenant_code`, every visit to e.g.
 * `/login` would ambiguously match either the global login OR that
 * tenant's home. The backend's tenant-create validator (in
 * `backend/modules/super_admin/api/routes.py`) MUST reject any
 * tenant_code in this list (case-insensitive).
 *
 * Keep in sync with `RESERVED_TENANT_SLUGS` in the backend.
 */
import { PUBLIC_LEGAL_PAGES } from '../modules/legal/publicPaths';

export const RESERVED_TENANT_SLUGS = new Set<string>([
  // ── Tenant-less surfaces (these routes are mounted at root) ─────
  'login',
  'super-admin',
  'design-system',
  'settings',
  // The platform's public documents (/about, /privacy, /terms) and /legal.
  ...PUBLIC_LEGAL_PAGES,
  'legal',
  // Where a Google consent popup lands (it tells the opener and closes).
  'google-access',
  // Where the "Connect monday.com" popup lands (it says how and closes).
  'monday-connected',
  // Where a person's own sign-in to a connector lands (it says how and closes).
  'connector-connected',
  // ── Page names that ALSO live under /<tenant>/<page> ────────────
  // These have top-level shortcut routes that redirect to the
  // user's tenant version (so super_admin typing `/admin` lands
  // on `/<their company>/admin` instead of being misinterpreted as
  // tenant slug 'admin'). Without this, `/admin` would match the
  // `/:tenantSlug` pattern, mount MainPage thinking the tenant is
  // 'admin', and every subsequent sidebar click would build
  // `/admin/bots`, `/admin/flows`, etc. — the "admin sticks to next
  // page" bug the user reported.
  'admin',
  'bots',
  'agents',
  'chat',
  'flows',
  'insights',
  'cost-simulator',
  'logs',
  'skills',
  'data-bundles',
  'vault',
  'library',
  'projects',
  // ── Infrastructure paths ────────────────────────────────────────
  'api',
  'health',
  'static',
  'assets',
  // ── Legacy hash-router escape hatches — retained as redirect
  // targets by LegacyHashRedirect.tsx during the 90-day grace window.
  '_t',
  '_',
]);

export function isReservedSlug(slug: string | null | undefined): boolean {
  if (!slug) return false;
  return RESERVED_TENANT_SLUGS.has(slug.toLowerCase());
}
